/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Textarea,
  Typography,
} from '@mui/joy';
import { useAuth } from 'renderer/lib/authContext';
import { useNotification } from 'renderer/components/Shared/NotificationSystem';

// Mirrors PROVIDER_NOTE_MAX_LENGTH on the API. Kept in sync by hand; the server
// rejects anything longer, so the counter here is a courtesy, not the guard.
const NOTE_MAX_LENGTH = 8000;

interface ProviderSteeringNoteModalProps {
  open: boolean;
  onClose: () => void;
  initialNote: string;
  onSaved?: () => void;
}

export default function ProviderSteeringNoteModal({
  open,
  onClose,
  initialNote,
  onSaved,
}: ProviderSteeringNoteModalProps) {
  const { fetchWithAuth } = useAuth();
  const { addNotification } = useNotification();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setNote(initialNote);
    setSaving(false);
  }, [open, initialNote]);

  const tooLong = note.length > NOTE_MAX_LENGTH;

  const handleSave = async () => {
    if (tooLong) {
      return;
    }

    setSaving(true);

    try {
      // A whole-resource replace: whatever is sent becomes the note, and an
      // empty body clears it. No omit-means-unchanged rule to reason about.
      const response = await fetchWithAuth('compute_provider/provider-note', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note: note.trim() ? note : null }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail =
          typeof errorData?.detail === 'string'
            ? errorData.detail
            : 'Could not save the provider steering note.';
        addNotification({ type: 'danger', message: detail });
        return;
      }

      addNotification({
        type: 'success',
        message: note.trim()
          ? 'Provider steering note saved.'
          : 'Provider steering note cleared.',
      });
      onSaved?.();
      onClose();
    } catch (error) {
      addNotification({
        type: 'danger',
        message: 'Could not save the provider steering note.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 700, maxWidth: '95vw' }}>
        <ModalClose />
        <DialogTitle>Provider Steering Note</DialogTitle>
        <DialogContent>
          <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 1.5 }}>
            Guidance for whoever — or whatever — is choosing where to run a job.
            One note covers every provider, because the useful thing to say is
            usually a comparison between them. Agents fetch it deliberately
            before picking hardware; it is not part of the provider list.
            Nothing here is parsed, so write it as you would say it.
          </Typography>

          <FormControl error={tooLong}>
            <FormLabel>Note</FormLabel>
            <Textarea
              aria-label="Provider steering note"
              minRows={8}
              maxRows={20}
              placeholder={
                'e.g. Ask RunPod first for H100s — best availability and cheapest.\n' +
                'No A10s on this account; use Lambda for those.\n' +
                'Spot capacity is unreliable after 5pm PT.'
              }
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
            />
            <FormHelperText>
              {tooLong
                ? `Too long by ${note.length - NOTE_MAX_LENGTH} characters (limit ${NOTE_MAX_LENGTH}).`
                : `${note.length}/${NOTE_MAX_LENGTH} characters. Leave empty to clear.`}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="plain" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={tooLong}>
            Save Note
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
